import { AnnotationUploadResponse } from '@lunit-io/ctl-data-server';
import { AxiosResponse } from 'axios';

import { Modality } from 'src/types/api/data/image';
import { JobReadSchema, JobType } from 'src/types/api/data/job';
import {
  ProjectCreateSchema,
  ProjectListSchema,
  ProjectReadSchema,
  ClaimCreateSchema,
  AnnotatorSchema,
  ReviewerSchema,
} from 'src/types/api/data/project';
import { ClientProject, ClientProjectList } from 'src/types/client/project';
import { Annotator, Reviewer, User } from 'src/types/client/user';
import { ClientErrorCode, ClientError } from 'src/utils/clientError';
import { dataApi } from 'src/utils/http';

import { getUsersInfo } from './account';
import { shouldUseV3Api } from './helpers';
import { getJobStats, getProjectJobStats } from './stats';

export const DEFAULT_STAT = {
  total: 0,
  createdStage: 0,
  assignedStage: 0,
  queuedStage: 0,
  completedStage: 0,
  reportedStage: 0,
};

export const DEFAULT_JOB_STATS = {
  total: DEFAULT_STAT,
  annotation: DEFAULT_STAT,
  cross: DEFAULT_STAT,
};

export const getProject = async (
  projectId: string,
  modality: Modality
): Promise<ClientProject> => {
  try {
    const { data: project } = await dataApi.get<ProjectReadSchema>(
      `/projects/${projectId}/`,
      {
        params: {
          all: true,
          modality: modality.toLowerCase(),
        },
      }
    );

    const jobStats = await getProjectJobStats({
      projectId: project.id,
      modality,
    });

    return {
      ...project,
      jobStats,
    };
  } catch (error) {
    throw new ClientError({
      code: ClientErrorCode.UNEXPECTED,
      message: (error as ClientError).message,
    });
  }
};

export type ProjectListProps = {
  modality: Modality;
  page: number;
  pageSize: number;
  isAdmin: boolean;
  sort: string;
};

export const getProjectList = async ({
  modality,
  page,
  pageSize,
  isAdmin,
  sort,
}: ProjectListProps): Promise<ClientProjectList> => {
  // Only the admin user is allowed
  // For non-admin user, list the project that he is included
  const { data: projectListInfo } = await dataApi.get<ProjectListSchema>(
    `/v3/projects/`,
    {
      params: {
        all: isAdmin,
        page,
        page_size: pageSize,
        sort,
        modality: modality.toLowerCase(),
      },
    }
  );
  const { results, ...listInfo } = projectListInfo;
  const statResponses = await Promise.all(
    results.map(async project => {
      let jobStats = DEFAULT_JOB_STATS;
      try {
        jobStats = await getProjectJobStats({
          projectId: project.id,
          modality,
        });
      } catch (error) {
        // If getProjectJobStats fails, use default stats
      }
      return {
        ...project,
        jobStats,
      };
    })
  );
  return { ...listInfo, projects: statResponses };
};

type ClaimUpdateSchema = Omit<ClaimCreateSchema, 'assets'> & {
  assets: Array<
    Omit<ClaimCreateSchema['assets'][number], 'id'> & { id?: string }
  >;
};

export const configureProject = async (
  projectId: string,
  modality: Modality,
  claim: ClaimCreateSchema
): Promise<ProjectReadSchema> => {
  const objId = /^[0-9a-fA-F]{24}$/;
  const sanitizedClaim: ClaimUpdateSchema = {
    ...claim,
    assets: claim.assets.map(({ id, ...rest }) =>
      id && objId.test(id) ? { ...rest, id } : rest
    ),
  };
  const { data } = await dataApi.patch<
    ProjectReadSchema,
    AxiosResponse<ProjectReadSchema>,
    { claim: ClaimUpdateSchema } // id can be omitted for newly added assets
  >(
    `/v3/projects/${projectId}`,
    { claim: sanitizedClaim },
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );
  return data;
};

export type ExportAnnotationsPurpose = 'INVOICING' | 'TRAINING' | 'QC';

export const exportAnnotations = async (
  projectId: string,
  modality: Modality,
  purpose: ExportAnnotationsPurpose
): Promise<void> => {
  const invoicingConfig = {
    format: 'csv',
    withTimeSpent: true,
  };
  const trainingConfig = {
    format: 'json',
    withTimeSpent: false,
  };
  const qualityControlConfig = {
    format: 'csv',
    withTimeSpent: false,
  };
  const config =
    purpose === 'INVOICING'
      ? invoicingConfig
      : purpose === 'TRAINING'
      ? trainingConfig
      : qualityControlConfig;

  const { data } = await dataApi.get(
    `/v3/projects/${projectId}/annotations/export`,
    {
      params: {
        modality: modality.toLowerCase(),
        ...config,
      },
      responseType: 'blob',
    }
  );
  // Create a temporary URL for the Blob
  const url = URL.createObjectURL(data);

  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  // Set the filename for the downloaded file
  downloadLink.download = `${modality}_project_${projectId}_annotations.${config.format}`;

  // Open the download dialog
  downloadLink.click();

  // Clean up the temporary URL
  URL.revokeObjectURL(url);
};

export const getAnnotatorList = async (
  projectId: string,
  modality: Modality,
  annotators?: AnnotatorSchema[]
): Promise<Annotator[]> => {
  // If annotators are provided, use them; otherwise fetch from project
  const annotatorSchemas =
    annotators ?? (await getProject(projectId, modality)).annotators;

  // Fetch all users once and create a lookup map
  const allUsers = await getUsersInfo();
  const userMap = new Map(allUsers.map(user => [user.id, user]));

  const annotatorList = await Promise.all(
    annotatorSchemas.map(async annotator => {
      const userInfo = userMap.get(annotator.id);
      if (!userInfo) {
        throw new Error(`User not found: ${annotator.id}`);
      }

      const [annotationStat, crossJobStat] = await Promise.all([
        getJobStats({
          projectId,
          annotatorId: annotator.id,
          type: 'annotation',
          modality,
        }),
        getJobStats({
          projectId,
          annotatorId: annotator.id,
          type: 'cross',
          modality,
        }),
      ]);

      return {
        ...userInfo,
        jobStat: annotationStat.user ?? DEFAULT_STAT,
        crossJobStat: crossJobStat.user ?? DEFAULT_STAT,
      };
    })
  );

  return annotatorList;
};

type AddCaseWithQueryProps = {
  projectId: string;
  modality: string;
  type: JobType;
  numOfCrossJobs?: number;
  caseIds: string[];
  datasetId?: string;
};

export const addV3CasesWithQuery = async ({
  projectId,
  type,
  modality,
  numOfCrossJobs,
  datasetId,
}: AddCaseWithQueryProps): Promise<JobReadSchema[]> => {
  const payload = {
    project: projectId,
    dataset: datasetId,
    jobType: type,
    multiplier: numOfCrossJobs,
  };

  const { data } = await dataApi.post(
    `v3/projects/${projectId}/jobs:bulk`,
    payload,
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );

  return data;
};

export const addV2CasesWithQuery = async ({
  projectId,
  type,
  caseIds,
  modality,
  numOfCrossJobs,
}: AddCaseWithQueryProps): Promise<JobReadSchema[]> => {
  const payload = {
    operation: 'CreateJobs',
    data: {
      caseIds,
      jobType: type,
      numOfCrossJobs,
    },
  };

  const { data } = await dataApi.post(
    `v2/bulk/projects/${projectId}/jobs/`,
    payload,
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );

  return data;
};

export const addCasesWithQuery = async (
  args: AddCaseWithQueryProps
): Promise<JobReadSchema[]> => {
  const useV3 = await shouldUseV3Api(args.projectId, args.modality);
  if (useV3) {
    return addV3CasesWithQuery(args);
  }
  return addV2CasesWithQuery(args);
};

type AddCaseWithCsvProps = {
  projectId: string;
  modality: Modality;
  type: JobType;
  numOfCrossJobs?: number;
  caseIds: string[];
  pairCaseIds: string[];
};

export const addCPCCasesWithCsv = async ({
  projectId,
  type,
  modality,
  numOfCrossJobs,
  caseIds,
  pairCaseIds,
}: AddCaseWithCsvProps): Promise<JobReadSchema[]> => {
  const payload = {
    operation: 'CreatePairJobs',
    data: {
      caseIds,
      pairCaseIds,
      jobType: type,
      numOfCrossJobs: numOfCrossJobs,
    },
  };

  const { data } = await dataApi.post(
    `v2/bulk/projects/${projectId}/jobs/`,
    payload,
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );

  return data;
};

export const confirmProject = async (
  projectId: string,
  modality: Modality,
  allowSkipped?: boolean
): Promise<boolean> => {
  try {
    await dataApi.patch(
      `v3/projects/${projectId}/confirm/`,
      {},
      {
        params: {
          allow_skipped: allowSkipped,
          modality: modality.toLowerCase(),
        },
      }
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const getReviewerList = async (
  projectId: string,
  modality: Modality,
  reviewers?: ReviewerSchema[]
): Promise<Reviewer[]> => {
  // If reviewers are provided, use them; otherwise fetch from project
  const reviewerSchemas =
    reviewers ?? (await getProject(projectId, modality)).reviewers;

  // Fetch all users once and create a lookup map
  const allUsers = await getUsersInfo();
  const userMap = new Map(allUsers.map(user => [user.id, user]));

  const reviewerList = reviewerSchemas.map(reviewer => {
    const userInfo = userMap.get(reviewer.id);
    if (!userInfo) {
      throw new Error(`User not found: ${reviewer.id}`);
    }
    return {
      ...userInfo,
      associates: reviewer.associates,
    };
  });

  return reviewerList;
};

export const assignAssociatesToReviewer = async (
  projectId: string,
  reviewerId: string,
  associateIds: string[],
  modality: Modality
): Promise<void> => {
  await dataApi.post(
    `/v3/projects/${projectId}/reviewers/${reviewerId}/associates`,
    associateIds,
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );
};

export const unassignAssociateFromReviewer = async (
  projectId: string,
  reviewerId: string,
  associateId: string,
  modality: Modality
): Promise<void> => {
  await dataApi.delete(
    `/v3/projects/${projectId}/reviewers/${reviewerId}/associates/${associateId}`,
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );
};

// Deprecated: Associates already have user info from the API
// Kept for backward compatibility with existing Recoil state
export const getAssociateList = async (idList: string[]): Promise<User[]> => {
  const allUsers = await getUsersInfo();
  const userMap = new Map(allUsers.map(user => [user.id, user]));

  return idList.map(id => {
    const user = userMap.get(id);
    if (!user) {
      throw new Error(`User not found: ${id}`);
    }
    return user;
  });
};

export const purgeProject = async (
  projectId: string,
  modality: Modality,
  annotatorId: string
): Promise<boolean> => {
  return dataApi.delete(
    // TODO: Missing v3 API
    `/projects/${projectId}/user/${annotatorId}/annotations/`,
    { params: { modality: modality.toLowerCase() } }
  );
};

/* TODO: Remove this function and related components */
// Create Project should be done in AIPF Portal
export const createProject = async (
  payload: ProjectCreateSchema,
  modality: Modality
): Promise<ProjectReadSchema> => {
  const { data } = await dataApi.post<
    ProjectReadSchema,
    AxiosResponse<ProjectReadSchema>,
    ProjectCreateSchema
  >(`/projects/`, payload, {
    params: {
      modality: modality.toLowerCase(),
    },
  });

  return data;
};

// Clone Project should be done in AIPF Portal
export type ProjectClonePayload = {
  name: string;
  claim: boolean;
  annotator: boolean;
  job: boolean;
  reading: boolean;
  default_claim?: boolean;
  modality: Modality;
};

export const cloneProject = async (
  projectId: string,
  modality: Modality,
  payload: ProjectClonePayload
): Promise<boolean> => {
  const { data } = await dataApi.post(`/projects/${projectId}/clone`, payload, {
    params: {
      modality: modality.toLowerCase(),
    },
  });
  return data;
};

export const uploadAnnotations = async (
  projectId: string,
  modality: Modality
): Promise<AnnotationUploadResponse> => {
  const { data } = await dataApi.post<AnnotationUploadResponse>(
    `/v3/projects/${projectId}/annotations:upload`,
    {},
    {
      params: {
        modality: modality.toLowerCase(),
      },
    }
  );

  return data;
};
